import {
  WSButton,
  WSElement,
  WSPage,
  WSPageToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { WorkLogStatus } from "@wingspanhq/payments/dist/interfaces";
import { useMemo } from "react";
import { Redirect, Route, Switch, useHistory } from "react-router-dom";
import { Tabs } from "../../../components/Tabs";
import { useUserId } from "../../../query/hooks/helpers";
import { useFeatureFlags } from "../../../query/hooks/useFeatureFlags";
import { useQueryWorkLogsSummary } from "../../../query/workLog/queries/useQueryWorkLogsSummary";
import { WorklogsPayerDashboard } from "../components/WorklogsPayerDashboard";
import { FilterableTableWorkLogsPayerView } from "../components/TableWorkLogsPayerView/FilterableTableWorkLogsPayerView";
import { WorkLogPayerTabFilterStatus } from "../components/TableWorkLogsPayerView/filters/types";
import {
  PATH_WORK_LOGS_PAYER,
  PATH_WORK_LOGS_PAYER_TAB_APPROVED,
  PATH_WORK_LOGS_PAYER_TAB_DASHBOARD,
  PATH_WORK_LOGS_PAYER_TAB_REJECTED,
  PATH_WORK_LOGS_PAYER_TAB_IN_PROGRESS,
  PATH_WORK_LOGS_PAYER_TAB_TO_APPROVE
} from "../paths";

export const RoutePayerLogs = () => {
  const history = useHistory();
  const userId = useUserId();
  const featureFlags = useFeatureFlags();
  const isWorkLogReviewWorkflow = featureFlags.data?.workLogReviewWorkflow;

  // Summary queries for tab counts
  const queryToApprove = useQueryWorkLogsSummary({
    "filter[payerId]": userId,
    "filter[status][=]": "InReview" as WorkLogStatus
  });

  const queryApproved = useQueryWorkLogsSummary({
    "filter[payerId]": userId,
    "filter[status][=]": WorkLogStatus.Approved
  });

  const queryInProgress = useQueryWorkLogsSummary({
    "filter[payerId]": userId,
    "filter[status][=]": "InProgress" as WorkLogStatus
  });

  const queryRejected = useQueryWorkLogsSummary({
    "filter[payerId]": userId,
    "filter[status][=]": "Rejected" as WorkLogStatus
  });

  // Build tabs based on feature flag
  const tabs = useMemo(() => {
    if (isWorkLogReviewWorkflow) {
      // New experience: Dashboard + 4 status tabs
      return [
        {
          label: "Dashboard",
          path: PATH_WORK_LOGS_PAYER_TAB_DASHBOARD
        },
        {
          label: "To approve",
          path: PATH_WORK_LOGS_PAYER_TAB_TO_APPROVE,
          badge: queryToApprove.data?.listSize
        },
        {
          label: "Approved",
          path: PATH_WORK_LOGS_PAYER_TAB_APPROVED,
          badge: queryApproved.data?.listSize
        },
        {
          label: "Rejected",
          path: PATH_WORK_LOGS_PAYER_TAB_REJECTED,
          badge: queryRejected.data?.listSize
        },
        {
          label: "In-progress",
          path: PATH_WORK_LOGS_PAYER_TAB_IN_PROGRESS,
          badge: queryInProgress.data?.listSize
        }
      ];
    }

    // Legacy: No tabs (just show ToApprove table directly)
    return [];
  }, [
    isWorkLogReviewWorkflow,
    queryToApprove.data?.listSize,
    queryApproved.data?.listSize,
    queryInProgress.data?.listSize,
    queryRejected.data?.listSize
  ]);

  return (
    <WSPage>
      <WSElement mb="2XL">
        <WSPageToolbar title="Work logs" />
        <WSText.ParagraphSm color="gray500">
          {isWorkLogReviewWorkflow
            ? "Review contractor work logs before they become payables. Once approved, payables are automatically generated on your "
            : "See submitted by contractor work, payables are automatically generated on your "}
          <WSButton.Link
            onClick={() => history.push("/member/invoices/payables")}
          >
            Payables
          </WSButton.Link>{" "}
          page.
        </WSText.ParagraphSm>
      </WSElement>

      {isWorkLogReviewWorkflow ? (
        <>
          <Tabs mb="L" tabs={tabs} />
          <Switch>
            <Route
              path={PATH_WORK_LOGS_PAYER_TAB_DASHBOARD}
              component={WorklogsPayerDashboard}
            />
            <Route path={PATH_WORK_LOGS_PAYER_TAB_TO_APPROVE}>
              <FilterableTableWorkLogsPayerView
                status={WorkLogPayerTabFilterStatus.ToApprove}
                enableBulkActions={isWorkLogReviewWorkflow}
              />
            </Route>
            <Route path={PATH_WORK_LOGS_PAYER_TAB_APPROVED}>
              <FilterableTableWorkLogsPayerView
                status={WorkLogPayerTabFilterStatus.Approved}
              />
            </Route>
            <Route path={PATH_WORK_LOGS_PAYER_TAB_REJECTED}>
              <FilterableTableWorkLogsPayerView
                status={WorkLogPayerTabFilterStatus.Rejected}
              />
            </Route>
            <Route path={PATH_WORK_LOGS_PAYER_TAB_IN_PROGRESS}>
              <FilterableTableWorkLogsPayerView
                status={WorkLogPayerTabFilterStatus.InProgress}
              />
            </Route>
            <Redirect
              from={PATH_WORK_LOGS_PAYER}
              to={PATH_WORK_LOGS_PAYER_TAB_DASHBOARD}
            />
          </Switch>
        </>
      ) : (
        <FilterableTableWorkLogsPayerView
          status={WorkLogPayerTabFilterStatus.ToApprove}
        />
      )}
    </WSPage>
  );
};
